import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useStatusBarSurface } from '../hooks/useStatusBarSurface';
import {
	NETWORK_BLOCKED_MESSAGE,
	isPortalNetworkError,
} from '../lib/portalNetworkError';
import { resolveShortLink } from '../services/portalSiteService';

/**
 * PD-17144 — the short links in welcome, set-password, reset and sign-in
 * texts (`<this storefront>/s/<code>`). Texts used to carry an auth server
 * short link, so customers saw our domain instead of their supplier's; the
 * link now lives on the storefront and this page forwards to the page it
 * stands for (reset-password, sign-in-link, ...), which then works exactly
 * as it does when opened from the email.
 *
 * The target is followed only when it is on this same storefront. The API
 * already refuses anything else; checking again here keeps this page from
 * ever navigating off-site.
 */
export function ShortLinkPage(): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { code = '' } = useParams<{ code: string }>();
	// No header band: the status bar reads against this page (PD-16744).
	useStatusBarSurface('page');
	// PD-17406 — 'network': the lookup could not reach our servers at all, so
	// "try again in a moment" would not help; say what to try instead.
	const [status, setStatus] = useState<
		'resolving' | 'expired' | 'network' | 'error'
	>('resolving');

	useEffect(() => {
		let cancelled = false;

		void (async (): Promise<void> => {
			try {
				const target = code === '' ? null : await resolveShortLink(code);
				if (cancelled) {
					return;
				}
				const url = target != null ? new URL(target) : null;
				if (url == null || url.origin !== window.location.origin) {
					setStatus('expired');
					return;
				}
				// Same storefront, so an in-app navigation: no second page load.
				void navigate(`${url.pathname}${url.search}${url.hash}`, {
					replace: true,
				});
			} catch (error) {
				if (!cancelled) {
					setStatus(isPortalNetworkError(error) ? 'network' : 'error');
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [code, navigate]);

	if (status === 'resolving') {
		return (
			<div className="min-h-dvh flex items-center justify-center px-4 py-8 bg-brand-page-bg">
				<p className="text-sm text-brand-muted">{t('Opening your link...')}</p>
			</div>
		);
	}

	return (
		<div className="min-h-dvh flex items-center justify-center px-4 py-8 bg-brand-page-bg">
			<div className="w-full max-w-md bg-brand-surface border border-brand-border rounded-xl p-6 space-y-4">
				<h1 className="text-2xl font-semibold text-default">
					{status === 'expired'
						? t('This link has expired')
						: status === 'network'
							? t("We couldn't connect")
							: t('Something went wrong')}
				</h1>
				<p className="text-sm text-brand-muted">
					{status === 'expired'
						? t(
								'Links in our texts work for a limited time. Go to sign in and request a new one.',
							)
						: status === 'network'
							? t(NETWORK_BLOCKED_MESSAGE)
							: t(
									'We could not open this link right now. Please try again in a moment.',
								)}
				</p>
				<button
					type="button"
					onClick={() => {
						void navigate('/', { replace: true });
					}}
					className="inline-block rounded-md bg-portal-accent hover:bg-portal-accent-hover text-white font-medium px-4 py-2 transition-colors"
				>
					{t('Go to sign in')}
				</button>
			</div>
		</div>
	);
}
